'use client';

import type React from 'react';

declare global {
  interface Window {
    onYouTubeIframeAPIReady?: () => void;
  }
}
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import ForgotPasswordModal from '../V2/ForgotPasswordModal';
import LoginModal from '../V2/LoginModal';
import SignUpModal from '../V2/SignUpModal';
import { authModalControl } from '../../atoms/authModalControl';
import { user } from '../../atoms/user';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import { User } from '../../utils/interfaces/User';

interface CustomYouTubePlayerProps {
  videoId: string;
  token?: string;
  isRounded?: boolean;
  onTimeUpdate?: (time: number) => void;
  autoPlay?: boolean;
  hideControls?: boolean;
  isFullscreen?: boolean;
  setIsPlaying: (value: boolean) => void;
  isPlaying: boolean;
  playerRef: any;
}

const YoutubeLivePlayer: React.FC<CustomYouTubePlayerProps> = ({
  videoId,
  token,
  isRounded = false,
  onTimeUpdate,
  autoPlay = false,
  hideControls = false,
  isFullscreen = false,
  setIsPlaying,
  playerRef,
}) => {
  const [visible, setVisible] = useRecoilState(authModalControl);
  const userData: User | undefined = useRecoilValue(user);
  const loginTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef<number | null>(null);
  const accumulatedTimeRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout>();
  const router = useRouter();
  const { workshop_id, watchId } = router.query;

  useEffect(() => {
    if (document && !document.getElementById('youtube-api')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      tag.id = 'youtube-api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      }
    }

    const initializePlayer = () => {
      if (
        typeof window !== 'undefined' &&
        window.YT &&
        typeof window.YT.Player === 'function'
      ) {
        playerRef.current = new window.YT.Player('youtube-player-container', {
          videoId,
          playerVars: {
            controls: hideControls === true ? 0 : 1,
            modestbranding: 1,
            rel: 0,
            iv_load_policy: 3,
            showinfo: 0,
            enablejsapi: 1,
            origin: window.location.origin,
            disablekb: 1,
            fs: 0,
            playsinline: 1,
            autoplay: 1,
            mute: 0,
          },
          events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange,
          },
        });
      }
    };

    const onPlayerReady = () => {
      if (containerRef.current) {
        containerRef.current.addEventListener('mouseenter', showControls);
        containerRef.current.addEventListener('mouseleave', hidePlayerControls);
      }
      if (
        autoPlay &&
        playerRef.current &&
        typeof playerRef.current.playVideo === 'function'
      ) {
        playerRef.current.playVideo();
      }

      if (containerRef.current) {
        containerRef.current.addEventListener('mouseenter', showControls);
        containerRef.current.addEventListener('mouseleave', hidePlayerControls);
      }
    };

    const onPlayerStateChange = (event: any) => {
      const PlayerState = window.YT.PlayerState;

      // Manage playing/paused states
      if (event.data === PlayerState.PLAYING) {
        setIsPlaying(true);
        startTimer();
      } else if (event.data === PlayerState.PAUSED) {
        setIsPlaying(false);
        stopTimer();
      } else if (
        event.data === PlayerState.ENDED ||
        event.data === PlayerState.BUFFERING ||
        event.data === PlayerState.CUED
      ) {
        setIsPlaying(false);
        clearInterval(intervalRef.current);
      }

      // Trigger login modal if video plays without token
      if (
        event.data === PlayerState.PLAYING &&
        !token &&
        router.pathname.includes('watch')
      ) {
        if (loginTimeoutRef.current) {
          clearTimeout(loginTimeoutRef.current);
        }

        loginTimeoutRef.current = setTimeout(() => {
          if (
            playerRef.current &&
            typeof playerRef.current.pauseVideo === 'function'
          ) {
            playerRef.current.pauseVideo();

            setVisible((prev) => {
              if (prev.login) {
                return { ...prev, login: false };
              }
              return { ...prev, login: true };
            });

            setTimeout(() => {
              setVisible((prev) => ({ ...prev, login: true }));
            }, 50);
          }
        }, 30000);
      }
    };

    const startTimer = () => {
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now();
      }
    };

    const stopTimer = () => {
      if (startTimeRef.current) {
        const elapsed = Date.now() - startTimeRef.current;
        accumulatedTimeRef.current += elapsed;
        startTimeRef.current = null;
        logSessionTime(accumulatedTimeRef.current);
      }
    };
    const showControls = () => {
      if (playerRef.current) {
        playerRef.current
          .getIframe()
          ?.contentWindow?.postMessage(
            '{"event":"command","func":"showControls","args":""}',
            '*',
          );
      }
    };

    const logSessionTime = (time: number) => {
      const eventParams: EventDetails = {
        event_name: 'play',
        source: 'live',
        source_type: 'workshop',
        source_id: workshop_id as string,
        sub_source: 'session',
        sub_source_id: watchId as string,
        unit: 'seconds',
        value: time / 1000,
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });
    };

    const hidePlayerControls = () => {
      if (playerRef.current) {
        setTimeout(() => {
          playerRef.current
            .getIframe()
            ?.contentWindow?.postMessage(
              '{"event":"command","func":"hideControls","args":""}',
              '*',
            );
        }, 2000);
      }
    };

    if (typeof window !== 'undefined') {
      if (window.YT && typeof window.YT.Player === 'function') {
        initializePlayer();
      } else {
        window.onYouTubeIframeAPIReady = initializePlayer;
      }
    }

    // Cleanup
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('mouseenter', showControls);
        containerRef.current.removeEventListener(
          'mouseleave',
          hidePlayerControls,
        );
      }

      clearInterval(intervalRef.current);
      if (loginTimeoutRef.current) {
        clearTimeout(loginTimeoutRef.current);
      }

      if (
        playerRef.current &&
        typeof playerRef.current.destroy === 'function'
      ) {
        playerRef.current.destroy();
      }
    };
  }, [videoId, token, onTimeUpdate, autoPlay, hideControls]);
  const onCancelCloseModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      signUp: false,
      subHeadingCopy: '',
      headingCopy: '',
    }));
    if (
      playerRef.current &&
      typeof playerRef.current.pauseVideo === 'function'
    ) {
      playerRef.current.playVideo();
    }
  };

  const onCancelCloseLoginModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      login: false,
      subHeadingCopy: '',
      headingCopy: '',
    }));
    if (
      playerRef.current &&
      typeof playerRef.current.pauseVideo === 'function'
    ) {
      playerRef.current.playVideo();
    }
  };
  const onCancelCloseForgotPasswordModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      forgotPassword: false,
    }));
    if (
      playerRef.current &&
      typeof playerRef.current.pauseVideo === 'function'
    ) {
      playerRef.current.playVideo();
    }
  };

  return (
    <>
      <SignUpModal
        onCancel={onCancelCloseModal}
        visible={visible.signUp}
        noRedirect={true}
        isClosable={false}
      />
      <LoginModal
        isClosable={false}
        onCancel={onCancelCloseLoginModal}
        visible={visible.login}
      />
      <ForgotPasswordModal
        onCancel={onCancelCloseForgotPasswordModal}
        visible={visible.forgotPassword}
        isClosable={false}
      />

      <div
        ref={containerRef}
        className={clsx(
          isFullscreen
            ? ' w-full h-full'
            : 'w-full md:h-[360px] lg:h-[440px] xl:h-[520px] no-select',
          `relative  overflow-hidden ${isRounded ? 'rounded-xl' : ''}`,
        )}
      >
        <div className='absolute inset-0 pointer-events-none z-20 opacity-0 hover:opacity-100 transition-opacity duration-300' />

        <div id='youtube-player-container' className='w-full h-full' />
      </div>
    </>
  );
};

export default YoutubeLivePlayer;
